import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import type { FilterFieldConfig, FilterValue } from '../../config/filterable-fields';
import { BOOLEAN_FILTER_OPTIONS } from '../../types/contacts-filters.types';

type ContactsCustomFieldFilterSectionProps = {
  field: FilterFieldConfig;
  value: FilterValue | undefined;
  onChange: (value: FilterValue) => void;
};

const toggleStringValue = (values: string[], value: string, checked: boolean): string[] => {
  if (checked) return values.includes(value) ? values : [...values, value];
  return values.filter((entry) => entry !== value);
};

const toStringArray = (value: FilterValue | undefined): string[] => {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string' && Boolean(entry));
  }
  if (typeof value === 'string' && value.trim()) {
    return [value];
  }
  return [];
};

const usesRangeOperators = (field: FilterFieldConfig): boolean =>
  field.operators.includes('numberRange') || field.operators.includes('dateRange');

export const isCustomFieldFilterActive = (
  field: FilterFieldConfig,
  value: FilterValue | undefined
): boolean => {
  if (value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object' && value !== null) {
    return Boolean('from' in value && value.from) || Boolean('to' in value && value.to);
  }
  return true;
};

export const countActiveCustomFieldFilters = (
  fields: Record<string, FilterValue>,
  customFields: FilterFieldConfig[]
): number =>
  customFields.filter((field) => isCustomFieldFilterActive(field, fields[field.key])).length;

export const ContactsCustomFieldFilterSection = ({
  field,
  value,
  onChange,
}: ContactsCustomFieldFilterSectionProps) => {
  const sectionId = `contacts-filter-custom-${field.key}`;

  if (field.valueType === 'boolean') {
    const selectedValues = toStringArray(value);

    return (
      <div className="mb-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {field.label}
        </p>
        <div className="flex flex-col gap-2">
          {BOOLEAN_FILTER_OPTIONS.map((option) => {
            const id = `${sectionId}-${option.value}`;
            return (
              <div key={option.value} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={selectedValues.includes(option.value)}
                  onCheckedChange={(checked) =>
                    onChange(toggleStringValue(selectedValues, option.value, checked === true))
                  }
                />
                <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                  {option.label}
                </Label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.options?.length) {
    const selectedValues = toStringArray(value);

    return (
      <div className="mb-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {field.label}
        </p>
        <div className="flex flex-col gap-2">
          {field.options.map((option) => {
            const id = `${sectionId}-${option.value}`;
            return (
              <div key={option.value} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={selectedValues.includes(option.value)}
                  onCheckedChange={(checked) =>
                    onChange(toggleStringValue(selectedValues, option.value, checked === true))
                  }
                />
                <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                  {option.label}
                </Label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (usesRangeOperators(field)) {
    const rangeValue =
      typeof value === 'object' && value !== null && !Array.isArray(value)
        ? (value as { from?: string; to?: string })
        : { from: '', to: '' };
    const inputType = field.valueType === 'number' ? 'number' : 'date';

    return (
      <div className="mb-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {field.label}
        </p>
        <div className="flex items-center gap-2">
          <Input
            id={`${sectionId}-from`}
            type={inputType}
            aria-label={`${field.label} from`}
            value={rangeValue.from ?? ''}
            onChange={(event) => onChange({ from: event.target.value, to: rangeValue.to ?? '' })}
            className="h-9 min-w-0 flex-1"
          />
          <span className="shrink-0 text-muted-foreground">–</span>
          <Input
            id={`${sectionId}-to`}
            type={inputType}
            aria-label={`${field.label} to`}
            value={rangeValue.to ?? ''}
            onChange={(event) => onChange({ from: rangeValue.from ?? '', to: event.target.value })}
            className="h-9 min-w-0 flex-1"
          />
        </div>
      </div>
    );
  }

  const inputType =
    field.valueType === 'number' ? 'number' : field.valueType === 'date' ? 'date' : 'text';
  const inputValue = typeof value === 'string' ? value : '';

  return (
    <div className="mb-3">
      <Label
        htmlFor={sectionId}
        className="mb-2 block text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        {field.label}
      </Label>
      <Input
        id={sectionId}
        type={inputType}
        value={inputValue}
        onChange={(event) => onChange(event.target.value)}
        className="h-9"
      />
    </div>
  );
};
